import type { AiJob, AiJobSummary, AiReportType } from '@fin/shared';
import { useEffect, useRef, useState } from 'react';
import { get } from './api.ts';
import { invalidate } from './query.ts';

/** how a ready report looks in the list: tiles in the category tile palette (lib/icons.tsx) */
export const reportLook: Record<AiReportType, { icon: string; color: string; about: string }> = {
  period: { icon: 'budget', color: '#5EE6A8', about: 'План и факт периода, траты вне плана, перерасход лимитов и прогноз до его конца.' },
  forecast: { icon: 'calendar', color: '#7EB6FF', about: 'Свободные деньги, темп трат вне плана, неоплаченные счета и события до зарплаты.' },
  month: { icon: 'chart', color: '#B69CFF', about: 'Доходы и расходы месяца против среднего за три месяца, главные и выросшие категории.' },
  anomalies: { icon: 'bolt', color: '#F2C14E', about: 'Всплески по категориям, крупные разовые траты и одинаковые операции за 30 дней.' },
  subscriptions: { icon: 'repeat', color: '#F08BC6', about: 'Платежи с одинаковой заметкой из месяца в месяц: что подорожало и что пропало.' },
  year: { icon: 'trend', color: '#4FD1C5', about: 'Доходы, расходы и отложенное по месяцам, вложения в инвестиции и выплаты по долгам.' },
};
export const questionLook = { icon: 'question', color: '#A1A1AA' };
export const brokerLook = { icon: 'briefcase', color: '#F49D6E' };
export const jobLook = (j: Pick<AiJobSummary, 'kind' | 'reportType'>) =>
  j.kind === 'report' && j.reportType ? reportLook[j.reportType] : j.kind === 'broker' ? brokerLook : questionLook;

export const isActive = (j: Pick<AiJobSummary, 'status'>) => j.status === 'queued' || j.status === 'running';

/** one job, refreshed every two seconds while Codex works on it */
export function useAiJob(id: number | null) {
  const [job, setJob] = useState<AiJob | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);
  const seq = useRef(0);
  useEffect(() => {
    if (id === null) { setJob(null); return; }
    const mine = ++seq.current;
    let timer = 0;
    const load = async () => {
      try {
        const next = await get<AiJob>(`/ai/jobs/${id}`);
        if (mine !== seq.current) return;
        setJob(next); setError(null);
        if (isActive(next)) timer = window.setTimeout(load, 2000);
        else invalidate('/ai/jobs');
      } catch (e) {
        if (mine !== seq.current) return;
        setError(e as Error);
        timer = window.setTimeout(load, 5000);
      }
    };
    void load();
    return () => { seq.current++; clearTimeout(timer); };
  }, [id, tick]);
  return { job: job?.id === id ? job : null, error, reload: () => setTick((t) => t + 1) };
}

/**
 * A screenshot for the broker reading: decoded by the browser and drawn again as JPEG, so a HEIC photo works,
 * the size stays small and the file's metadata (place, device) never leaves the phone.
 */
export async function screenshotToJpeg(file: Blob, maxSide = 2400): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode().catch(() => { throw new Error('Не удалось открыть картинку'); });
    const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
    for (const quality of [0.88, 0.75, 0.6]) {
      const data = canvas.toDataURL('image/jpeg', quality);
      // 3 MB of bytes is about 4 MB of base64
      if (data.length < 4_000_000) return data;
    }
    throw new Error('Скриншот слишком большой');
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** amounts and percents inside Codex's text get the money font */
export function splitMoney(text: string): { text: string; money: boolean }[] {
  const out: { text: string; money: boolean }[] = [];
  const re = /[−-]?\d{1,3}(?:[   ]\d{3})*(?:,\d+)?\s?[₽$€₸₮]|[−-]?\d+(?:,\d+)?%/g;
  let last = 0;
  for (const m of text.matchAll(re)) {
    if ((m.index ?? 0) > last) out.push({ text: text.slice(last, m.index), money: false });
    out.push({ text: m[0], money: true });
    last = (m.index ?? 0) + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last), money: false });
  return out;
}
